import { ChevronDown, Check } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { Button } from "@themes/pollinator/ui/Button";
import { Dropdown } from "@themes/pollinator/ui/Dropdown";
import { Type } from "@themes/pollinator/ui/Type";
import { UnreadDot } from "@themes/pollinator/ui/UnreadDot";
import type { ChatAvailableCircle } from "@core/features/chat";

export type Props = {
  /** All circles the user can chat with in the active program. */
  availableCircles: ChatAvailableCircle[];
  /** The currently selected circle's id. */
  currentCircleId: number;
  /**
   * TalkJS conversation ids that currently have unread messages.
   * Drives the dot on each row whose `conversation_id` is in this set
   * and the trigger-side dot when any *non-current* row is in the set
   * (i.e. there's unread the user would have to switch to find).
   */
  unreadConversationIds?: ReadonlySet<string>;
  /** Fired with the new circle's id when the user picks a different row. */
  onSelect: (circleId: number) => void;
};

/**
 * Compact "you can change" affordance for the chat sheet header. Renders
 * the current circle's name as a button; clicking opens a dropdown of
 * the user's other chat-eligible circles. Picking one closes the menu
 * and the conversation surface re-renders against the new selection.
 *
 * Only meaningful when the user has more than one circle to choose from
 * — the parent surface should not render this for single-circle users.
 */
export function ChatCircleSwitcher({
  availableCircles,
  currentCircleId,
  unreadConversationIds,
  onSelect,
}: Props) {
  const current = availableCircles.find((c) => c.circle_id === currentCircleId);

  const triggerLabel = current?.name ?? "Select a circle";

  // Trigger dot lights up only when an unread exists in a circle the
  // user would have to switch to — the current circle's own unreads are
  // already in view in the chatbox below the trigger, so flagging them
  // here would just be noise.
  const triggerHasUnread =
    unreadConversationIds != null &&
    availableCircles.some(
      (c) =>
        c.circle_id !== currentCircleId &&
        unreadConversationIds.has(c.conversation_id)
    );

  return (
    <Dropdown
      data-testid="chat-circle-switcher"
      placement="bottom-start"
      triggerClassName="w-fit"
      trigger={
        <Button
          variant="link"
          iconComponent={ChevronDown}
          iconPosition="right"
          className="px-0 text-left"
        >
          <span className="inline-flex items-center gap-2">
            {triggerLabel}
            <UnreadDot hasUnread={triggerHasUnread} />
            {triggerHasUnread && (
              <span className="sr-only">
                — unread messages in other circles
              </span>
            )}
          </span>
        </Button>
      }
    >
      <ul
        className="flex flex-col gap-1 min-w-48 py-1"
        role="list"
        data-testid="chat-circle-switcher-menu"
      >
        {availableCircles.map((circle) => {
          const isCurrent = circle.circle_id === currentCircleId;
          const hasUnread =
            unreadConversationIds?.has(circle.conversation_id) ?? false;
          return (
            <li key={circle.circle_id}>
              <button
                type="button"
                onClick={() => {
                  if (!isCurrent) {
                    onSelect(circle.circle_id);
                  }
                }}
                aria-current={isCurrent ? "true" : undefined}
                className={twMerge(
                  "w-full flex flex-row items-center gap-2 px-3 py-2 rounded text-left",
                  isCurrent
                    ? "bg-brand-100 text-brand-900"
                    : "hover:bg-brand-50 text-brand-800"
                )}
                data-testid={`chat-circle-switcher-option-${circle.circle_id}`}
              >
                <span className="flex-1 inline-flex items-center gap-2">
                  <Type variant="body2">{circle.name}</Type>
                  <UnreadDot hasUnread={hasUnread} />
                  {hasUnread && (
                    <span className="sr-only">— unread</span>
                  )}
                </span>
                {isCurrent && (
                  <Check className="w-4 h-4 text-brand-700" aria-hidden="true" />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </Dropdown>
  );
}

export default ChatCircleSwitcher;
